import {
  all,
  call, put, select, takeEvery,
} from 'redux-saga/effects';
import { request } from 'utils/request';
import { downloadSessionCertificatesCsv } from 'utils/csv-utils';
import { IRestApiResponse } from 'interfaces';
import { selectAuthUser } from 'base/auth/selectors';
import { currencyFormat } from 'utils/formats';
import { CoreSliceActions } from 'base/core/slice';
import API from './constants';

import { sessionSummaryActions } from './slice';
import {
  IBulkIssueSessionCertificateAction,
  IExportSessionCertificatesAction,
  IGenerateSessionStateAction,
  IGetSessionCertificatesAction,
  IGetSessionCertificatesPayload,
  IIssueSessionCertificateAction,
  ISessionDeleteAction,
  ISessionSummaryAction,
  ISessionSummaryUpdateAction,
  ISupportAction,
} from './interface';
import { selectSession } from './selector';

const buildSessionCertificatesQueryParams = (
  payload?: Partial<IGetSessionCertificatesPayload>,
) => {
  const params = new URLSearchParams();

  if (typeof payload?.page !== 'undefined') {
    params.append('page', payload.page.toString());
  }
  if (typeof payload?.limit !== 'undefined') {
    params.append('limit', payload.limit.toString());
  }
  if (payload?.user?.trim()) {
    params.append('user', payload.user.trim());
  }
  if (payload?.status) {
    params.append('status', payload.status.toString());
  }
  if (typeof payload?.issued !== 'undefined') {
    params.append('issued', payload.issued.toString());
  }
  if (payload?.completion) {
    params.append('completion', payload.completion.toString());
  }

  return params;
};

const buildPathWithQueryParams = (path: string, params: URLSearchParams) => {
  const queryString = params.toString();

  return queryString !== '' ? `${path}?${queryString}` : path;
};

const resolvePath = (path: string, sessionId: string) => path.replace(':sessionId', sessionId);

export function* fetchSessionSummaryGenerator({ payload }: ISessionSummaryAction): any {
  try {
    const authUser = yield select(selectAuthUser);
    const response: IRestApiResponse = yield call(
      request,
      { path: `${API.GET_FETCH_SESSION_SUMMARY.path}/${payload}`, method: API.GET_FETCH_SESSION_SUMMARY.method },
      null,
      true,
    );
    if (response.statusCode === 200) {
      const preferredCurrency = authUser?.attributes?.currency ?? 'AUD';
      const fee = yield call(currencyFormat, response.data.fee, response.data.currency ?? 'AUD', preferredCurrency);
      const subscriberFee = yield call(currencyFormat, response.data.subscriberFee, response.data.subscriberCurrency ?? 'AUD', preferredCurrency);
      const subscribeObserverFee = yield call(currencyFormat, response.data.subscribeObserverFee, response.data.subscribeObserverCurrency ?? 'AUD', preferredCurrency);

      const formattedData = yield {
        ...response.data, fee, subscriberFee, subscribeObserverFee,
      };
      yield put(sessionSummaryActions.fetchSessionSummarySucceeded(formattedData));
    } else {
      yield put(sessionSummaryActions.fetchSessionSummaryFailed());
    }
  } catch (error) {
    yield put(sessionSummaryActions.fetchSessionSummaryFailed());
  }
}

export function* getSessionCertificatesGenerator({
  payload,
}: IGetSessionCertificatesAction): any {
  try {
    const params = buildSessionCertificatesQueryParams(payload);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: buildPathWithQueryParams(
          resolvePath(API.GET_SESSION_CERTIFICATES.path, payload.sessionId),
          params,
        ),
        method: API.GET_SESSION_CERTIFICATES.method,
      },
      null,
      true,
    );

    if (response.statusCode === 200) {
      yield put(sessionSummaryActions.getSessionCertificatesSucceeded(response.data));
    } else {
      yield put(sessionSummaryActions.getSessionCertificatesFailed());
    }
  } catch (error) {
    yield put(sessionSummaryActions.getSessionCertificatesFailed());
  }
}

export function* exportSessionCertificatesGenerator({
  payload,
}: IExportSessionCertificatesAction): any {
  try {
    const session = yield select(selectSession);
    const params = buildSessionCertificatesQueryParams(payload);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: buildPathWithQueryParams(
          resolvePath(API.EXPORT_SESSION_CERTIFICATES.path, payload.sessionId),
          params,
        ),
        method: API.EXPORT_SESSION_CERTIFICATES.method,
      },
      null,
      true,
    );

    if (response.statusCode === 200 && Array.isArray(response.data)) {
      yield call(downloadSessionCertificatesCsv, {
        certificates: response.data,
        sessionTitle: session.title,
      });
      yield put(sessionSummaryActions.exportSessionCertificatesSucceeded());
      yield put(
        CoreSliceActions.setMessage({
          message: 'Certificates exported successfully',
          success: true,
        }),
      );
    } else {
      yield put(sessionSummaryActions.exportSessionCertificatesFailed());
      yield put(
        CoreSliceActions.setMessage({
          message: 'Certificate export failed',
          success: false,
        }),
      );
    }
  } catch (error) {
    yield put(sessionSummaryActions.exportSessionCertificatesFailed());
    yield put(
      CoreSliceActions.setMessage({
        message: 'Certificate export failed',
        success: false,
      }),
    );
  }
}

export function* getSessionUrlGenerator({ payload }: IGenerateSessionStateAction) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      { path: API.GET_SESSION_URL.path, method: API.GET_SESSION_URL.method },
      { ...payload },
      true,
      false,
    );

    if (response.statusCode === 201) {
      yield put(sessionSummaryActions.getSessionUrlSuccessed(response.data));
    } else {
      yield put(sessionSummaryActions.getSessionUrlFailed());
    }
  } catch (error) {
    yield put(sessionSummaryActions.getSessionUrlFailed());
  }
}

export function* issueSessionCertificateGenerator({
  payload,
}: IIssueSessionCertificateAction): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: API.ISSUE_SESSION_CERTIFICATE.path,
        method: API.ISSUE_SESSION_CERTIFICATE.method,
      },
      payload,
      true,
    );

    if (response.statusCode === 200 || response.statusCode === 201) {
      yield put(sessionSummaryActions.issueSessionCertificateSucceeded());
      yield put(
        CoreSliceActions.setMessage({
          message: 'Certificate issued successfully',
          success: true,
        }),
      );
      yield put(sessionSummaryActions.triggerRefetchSessionCertificates());
    } else {
      const failureMessage = response.message || 'Certificate issuance failed';
      yield put(sessionSummaryActions.issueSessionCertificateFailed());
      yield put(
        CoreSliceActions.setMessage({
          message: failureMessage,
          success: false,
        }),
      );
    }
  } catch (error) {
    const failureMessage = error instanceof Error && error.message
      ? error.message
      : 'Certificate issuance failed';
    yield put(sessionSummaryActions.issueSessionCertificateFailed());
    yield put(
      CoreSliceActions.setMessage({
        message: failureMessage,
        success: false,
      }),
    );
  }
}

export function* bulkIssueSessionCertificateGenerator({
  payload,
}: IBulkIssueSessionCertificateAction): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: API.BULK_ISSUE_SESSION_CERTIFICATE.path,
        method: API.BULK_ISSUE_SESSION_CERTIFICATE.method,
      },
      payload,
      true,
    );

    if (response.statusCode === 200 || response.statusCode === 201) {
      yield put(sessionSummaryActions.bulkIssueSessionCertificateSucceeded());
      yield put(
        CoreSliceActions.setMessage({
          message: 'Certificates issued successfully',
          success: true,
        }),
      );
      yield put(sessionSummaryActions.triggerRefetchSessionCertificates());
    } else {
      const failureMessage = response.message || 'Bulk certificate issuance failed';
      yield put(sessionSummaryActions.bulkIssueSessionCertificateFailed());
      yield put(
        CoreSliceActions.setMessage({
          message: failureMessage,
          success: false,
        }),
      );
    }
  } catch (error) {
    const failureMessage = error instanceof Error && error.message
      ? error.message
      : 'Bulk certificate issuance failed';
    yield put(sessionSummaryActions.bulkIssueSessionCertificateFailed());
    yield put(
      CoreSliceActions.setMessage({
        message: failureMessage,
        success: false,
      }),
    );
  }
}

export function* updateSessionSummeryGenerator({ payload }: ISessionSummaryUpdateAction): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      API.POST_UPDATE_SESSION,
      {
        id: payload.id,
        audienceType: payload?.audienceType,
        isActive: payload?.isActive,
        invitePeople: payload?.invitePeople,
      },
      true,
    );
    if (response.statusCode === 201) {
      yield put(sessionSummaryActions.updateSessionSummerySucceeded());
      if (payload?.resolve) {
        yield payload.resolve();
      }
    } else {
      yield put(sessionSummaryActions.updateSessionSummeryFailed());
      if (payload?.reject) {
        yield payload.reject();
      }
    }
  } catch (error) {
    yield put(sessionSummaryActions.updateSessionSummeryFailed());
    if (payload?.reject) {
      yield payload.reject();
    }
  }
}

export function* deleteSessionGenerator({ payload }: ISessionDeleteAction): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      { path: API.DELETE_DELETE_SESSION.path, method: API.DELETE_DELETE_SESSION.method },
      {
        id: payload.id,
        reasonForDeletion: payload.reasonForDeletion,
        linkedSessionId: payload.linkedSessionId,
      },
      true,
    );
    if (response.statusCode === 200) {
      yield put(sessionSummaryActions.deleteSessionSucceeded());
      yield call(payload.resolve);
    } else {
      yield put(sessionSummaryActions.deleteSessionFailed());
      yield call(payload.reject);
    }
  } catch (error) {
    yield put(sessionSummaryActions.deleteSessionFailed());
    yield call(payload.reject);
  }
}

export function* createSupportFeedback({ payload }: ISupportAction): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.POST_CREATE_SUPPORT_FEEDBACK.path}`,
        method: API.POST_CREATE_SUPPORT_FEEDBACK.method,
      },
      { ...payload },
      true,
    );
    if (response.statusCode === 201) {
      yield put(sessionSummaryActions.createSupportFeedbackSucceeded(response.data));
      yield put(
        CoreSliceActions.displayAlert({
          type: 'success',
          message: 'Support Feedback submitted successfully!',
          btnText: 'Ok',
        }),
      );
    } else {
      yield put(sessionSummaryActions.createSupportFeedbackFailed());
      yield put(
        CoreSliceActions.displayAlert({
          type: 'error',
          message: 'There was an error submitting the feedback.',
          btnText: 'Try submitting again',
        }),
      );
    }
  } catch (error) {
    yield put(sessionSummaryActions.createSupportFeedbackFailed());
    yield put(
      CoreSliceActions.displayAlert({
        type: 'error',
        message: 'There was an error submitting the feedback.',
        btnText: 'Try submitting again',
      }),
    );
  }
}

export function* sessionSammarySaga() {
  yield all([
    takeEvery(sessionSummaryActions.fetchSessionSummary.type, fetchSessionSummaryGenerator),
    takeEvery(sessionSummaryActions.getSessionCertificates.type, getSessionCertificatesGenerator),
    takeEvery(
      sessionSummaryActions.exportSessionCertificates.type,
      exportSessionCertificatesGenerator,
    ),
    takeEvery(sessionSummaryActions.updateSessionSummery.type, updateSessionSummeryGenerator),
    takeEvery(sessionSummaryActions.deleteSession.type, deleteSessionGenerator),
    takeEvery(sessionSummaryActions.getSessionUrl.type, getSessionUrlGenerator),
    takeEvery(sessionSummaryActions.createSupportFeedback.type, createSupportFeedback),
    takeEvery(
      sessionSummaryActions.issueSessionCertificate.type,
      issueSessionCertificateGenerator,
    ),
    takeEvery(
      sessionSummaryActions.bulkIssueSessionCertificate.type,
      bulkIssueSessionCertificateGenerator,
    ),
  ]);
}

export default sessionSammarySaga;
